<template>
  <div class="page-container">
    <main :class="['main-content', { nested: isMemberCenterContext }]">
      <div class="page-inner-box">
        <div class="order-detail-container">
          <div class="content-main">
            <!-- 订单详情卡片 -->
            <div class="order-detail-card">
              <div class="section-title">订单信息</div>
              <div class="divider"></div>
              
              <!-- 订单基本信息 -->
              <div class="order-basic-info">
                <div class="info-row">
                  <div class="info-label">订单编号：</div>
                  <div class="info-value">{{ order.id }}</div>
                </div>
                <div class="info-row">
                  <div class="info-label">订单日期：</div>
                  <div class="info-value">{{ order.orderDate }}</div>
                </div>
                <div class="info-row">
                  <div class="info-label">订单状态：</div>
                  <div class="info-value status-text" :class="{ 'status-danger': order.status === 'pendingPayment' && order.remainSeconds > 0 && order.remainSeconds <= 300 }">
                    {{ order.statusText }}
                    <span v-if="order.status === 'pendingPayment' && order.remainSeconds > 0" class="countdown-text" :class="{ 'countdown-danger': order.remainSeconds <= 300 }">
                      （{{ formatCountdown(order.remainSeconds) }}后自动关闭）
                    </span>
                  </div>
                </div>
              </div>
              
              <!-- 收货信息 -->
              <div class="section-title">收货信息</div>
              <div class="divider"></div>
              <div class="shipping-info">
                <div class="info-row">
                  <div class="info-label">收货人：</div>
                  <div class="info-value">{{ order.shippingInfo ? order.shippingInfo.name : '暂无' }}</div>
                </div>
                <div class="info-row">
                  <div class="info-label">联系电话：</div>
                  <div class="info-value">{{ order.shippingInfo ? order.shippingInfo.phone : '暂无' }}</div>
                </div>
                <div class="info-row">
                  <div class="info-label">收货地址：</div>
                  <div class="info-value">{{ order.shippingInfo ? order.shippingInfo.address : '暂无' }}</div>
                </div>
              </div>
              
              <!-- 商品信息 -->
              <div class="section-title">商品信息</div>
              <div class="divider"></div>
              <div class="product-info">
                <div class="product-item">
                  <div class="product-image" :style="{ backgroundImage: `url(${order.productImage})` }"></div>
                  <div class="product-details">
                    <div class="product-title">{{ order.productTitle }}</div>
                    <div class="product-spec">{{ order.productSpec }}</div>
                    <div class="product-price">¥{{ order.productPrice }}</div>
                  </div>
                  <div class="product-quantity">× {{ order.quantity }}</div>
                </div>
              </div>
              
              <!-- 发货信息 -->
              <template v-if="order.raw && order.raw.shipRemark">
                <div class="section-title">发货信息</div>
                <div class="divider"></div>
                <div class="shipping-info" style="margin-bottom: 30px;">
                  <div class="info-row">
                    <div class="info-label">发货备注：</div>
                    <div class="info-value">{{ order.raw.shipRemark }}</div>
                  </div>
                </div>
              </template>
              
              <!-- 退款信息 -->
              <template v-if="order.raw && (order.raw.refundReason || order.raw.refundImage)">
                <div class="section-title">退款信息</div>
                <div class="divider"></div>
                <div class="shipping-info" style="margin-bottom: 30px;">
                  <div class="info-row" v-if="order.raw.refundReason">
                    <div class="info-label">退款原因：</div>
                    <div class="info-value">{{ order.raw.refundReason }}</div>
                  </div>
                  <div class="info-row" v-if="order.raw.refundImage">
                    <div class="info-label">退款证明：</div>
                    <div class="info-value">
                      <el-image 
                        style="width: 100px; height: 100px"
                        :src="order.raw.refundImage" 
                        :preview-src-list="[order.raw.refundImage]">
                      </el-image>
                    </div>
                  </div>
                </div>
              </template>
              
              <!-- 订单金额 -->
              <div class="section-title">订单金额</div>
              <div class="divider"></div>
              <div class="order-amount">
                <div class="amount-row">
                  <div class="amount-label">商品金额：</div>
                  <div class="amount-value">¥{{ order.productPrice }}</div>
                </div>
                <div class="amount-row">
                  <div class="amount-label">运费：</div>
                  <div class="amount-value">¥{{ order.shippingFee }}</div>
                </div>
                <div class="amount-row total-row">
                  <div class="amount-label">订单总价：</div>
                  <div class="amount-value total-price">¥{{ order.totalPrice }}</div>
                </div>
              </div>
              
              <!-- 操作按钮 -->
              <div class="order-actions">
                <el-button type="primary" @click="handleConfirmReceipt" v-if="order.status === 'pendingReceipt'">确认收货</el-button>
                <el-button @click="backToOrders">返回订单列表</el-button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
export default {
  name: 'OrderDetail',
  computed: {
    isMemberCenterContext() {
      return this.$route.path.startsWith('/member-center')
    }
  },
  data() {
    return {
      orderId: null,
      order: {
        id: '',
        orderDate: '',
        status: '',
        statusText: '',
        productTitle: '',
        productSpec: '',
        productPrice: '',
        quantity: 0,
        totalItems: 0,
        totalPrice: '',
        shippingFee: '',
        shippingInfo: {
          name: '',
          phone: '',
          address: ''
        }
      },
      loading: false,
      error: null
    }
  },
  created() {
    // 从路由参数获取订单ID
    this.orderId = this.$route.params.id
    // 加载订单详情
    this.loadOrderDetail()
    this.startCountdownTimer()
  },
  beforeDestroy() {
    this.stopCountdownTimer()
  },
  methods: {
    normalizeOrder(item) {
      const id = (item && (item.id ?? item.orderId)) || this.orderId
      const orderNo = item && (item.orderNo ?? item.order_no)
      const orderDate = String((item && (item.createTime ?? item.payTime ?? item.updateTime)) || '')
      
      const amountRaw = item && (item.amount ?? item.cashAmount ?? item.payAmount)
      const amount = Number(amountRaw)
      const amountText = Number.isFinite(amount) ? amount.toFixed(2) : '—'
      
      const title =
        (item && (item.productName ?? item.courseName ?? item.paperName ?? item.productTitle ?? item.title ?? item.name)) ||
        (orderNo ? `订单号：${orderNo}` : `订单：${id}`)
      
      const productImage =
        (item && (item.productImage ?? item.courseImage ?? item.image ?? item.cover ?? item.coverImage)) ||
        require('@/assets/image/study_placeholder.png')
      
      const region = item && item.region
      const spec = region ? `地区：${region}` : ''
      
      const payStatus = item && (item.payStatus ?? item.pay_status ?? item.orderStatus)
      const remainSeconds = item && item.remainSeconds
      const isOverdue = item && item.isOverdue
      let isExpired = false
      if (payStatus === 0 || payStatus === '0') {
        if (isOverdue || (remainSeconds != null && remainSeconds <= 0)) {
          isExpired = true
        }
      }
      let statusText = '订单处理中'
      let status = 'processing'
      
      if (payStatus === 0 || payStatus === '0') {
        if (isExpired) {
          statusText = '已过期'
          status = 'expired'
        } else {
          statusText = '待付款'
          status = 'pendingPayment'
        }
      } else if (payStatus === 1 || payStatus === '1') {
        statusText = '已支付'
        status = 'paid'
      } else if (payStatus === 2 || payStatus === '2') {
        statusText = '已退款'
        status = 'refunded'
      } else if (payStatus === 3 || payStatus === '3') {
        statusText = '已完成'
        status = 'completed'
      } else if (payStatus === 4 || payStatus === '4') {
        statusText = '已退款'
        status = 'refunded'
      } else if (payStatus === 5 || payStatus === '5') {
        statusText = '订单关闭'
        status = 'closed'
      } else if (payStatus === 6 || payStatus === '6') {
        statusText = '退款中'
        status = 'refunding'
      }
      
      // Shipping info (if available)
      const shippingInfo = {
        name: (item && item.receiverName) || '暂无',
        phone: (item && item.receiverPhone) || '暂无',
        address: (item && item.receiverAddress) || '暂无'
      }

      return {
        id,
        orderNo,
        orderDate,
        status,
        statusText,
        remainSeconds: remainSeconds != null ? Math.max(0, Number(remainSeconds)) : null,
        productTitle: String(title || ''),
        productSpec: String(spec || ''),
        productImage,
        productPrice: amountText,
        quantity: 1,
        totalItems: 1,
        totalPrice: amountText,
        shippingFee: '0.00',
        shippingInfo,
        raw: item
      }
    },
    async loadOrderDetail() {
      this.loading = true
      try {
        const cacheKey = `snails_order_detail_${this.orderId}`
        const cached = localStorage.getItem(cacheKey)
        if (!cached) {
          this.order = this.normalizeOrder({ id: this.orderId })
          this.$message.error('未找到订单详情数据，请从订单列表进入')
          return
        }
        const data = JSON.parse(cached)
        this.order = this.normalizeOrder(data)
      } catch (err) {
        this.error = err
        this.$message.error((err && err.message) || '获取订单详情失败')
      } finally {
        this.loading = false
      }
    },
    // 返回订单列表
    backToOrders() {
      if (this.isMemberCenterContext) {
        this.$router.push('/member-center/orders')
      } else {
        this.$router.push('/my-orders')
      }
    },
    // 确认收货
    handleConfirmReceipt() {
      this.$confirm('确认已收到商品吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        // 模拟确认收货API请求
        setTimeout(() => {
          this.$message.success('确认收货成功！')
          // 更新订单状态
          this.order.status = 'completed'
          this.order.statusText = '已完成'
        }, 500)
      }).catch(() => {
        // 取消确认收货
      })
    },
    startCountdownTimer() {
      this.stopCountdownTimer()
      this._countdownTimer = setInterval(() => {
        if (this.order.status === 'pendingPayment' && this.order.remainSeconds > 0) {
          this.order.remainSeconds--
          if (this.order.remainSeconds <= 0) {
            this.order.status = 'expired'
            this.order.statusText = '已过期'
            this.stopCountdownTimer()
          }
        } else {
          this.stopCountdownTimer()
        }
      }, 1000)
    },
    stopCountdownTimer() {
      if (this._countdownTimer) {
        clearInterval(this._countdownTimer)
        this._countdownTimer = null
      }
    },
    formatCountdown(seconds) {
      if (seconds == null || seconds <= 0) return '0分0秒'
      const m = Math.floor(seconds / 60)
      const s = seconds % 60
      return `${m}分${s}秒`
    }
  }
}
</script>

<style scoped lang="scss">
.page-container { min-height: 100vh; display: flex; flex-direction: column; }
.main-content { flex: 1; padding-top: 20px; }
.main-content.nested { margin-top: 0; }
.main-content.nested .page-inner-box { width: 100%; margin: 0; }

.order-detail-container {
  display: flex;
  gap: 24px;
}

.content-main {
  flex: 1;
  background-color: #fff;
  min-height: 500px;
  padding: 20px;
}

.section-title {
  font-size: 20px;
  font-weight: 600;
  color: #3E2CCD;
  margin-bottom: 15px;
}

.divider {
  height: 1px;
  background-color: #eee;
  margin-bottom: 20px;
}

/* 订单基本信息 */
.order-basic-info {
  margin-bottom: 30px;
  
  .info-row {
    display: flex;
    margin-bottom: 15px;
    
    .info-label {
      width: 120px;
      font-size: 14px;
      color: #999;
    }
    
    .info-value {
      font-size: 14px;
      color: #333;
      
      &.status-text {
        color: #3E2CCD;
        font-weight: 500;
      }
    }
  }
}

/* 收货信息 */
.shipping-info {
  margin-bottom: 30px;
  
  .info-row {
    display: flex;
    margin-bottom: 15px;
    
    .info-label {
      width: 120px;
      font-size: 14px;
      color: #999;
    }
    
    .info-value {
      font-size: 14px;
      color: #333;
    }
  }
}

/* 商品信息 */
.product-info {
  margin-bottom: 30px;
  
  .product-item {
    display: flex;
    position: relative;
    
    .product-image {
      width: 120px;
      height: 90px;
      background-color: #f5f7fa;
      background-size: cover;
      background-position: center;
      background-repeat: no-repeat;
      border-radius: 4px;
      margin-right: 15px;
    }
    
    .product-details {
      flex: 1;
      padding-right: 50px;
      
      .product-title {
        font-size: 14px;
        color: #333;
        margin-bottom: 10px;
        line-height: 1.4;
      }
      
      .product-spec {
        font-size: 14px;
        color: #999;
        margin-bottom: 10px;
      }
      
      .product-price {
        font-size: 16px;
        color: #FF4433;
        font-weight: 500;
      }
    }
    
    .product-quantity {
      position: absolute;
      right: 0;
      top: 40px;
      font-size: 14px;
      color: #666;
    }
  }
}

/* 订单金额 */
.order-amount {
  margin-bottom: 30px;
  
  .amount-row {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 15px;
    
    .amount-label {
      width: 120px;
      font-size: 14px;
      color: #999;
      text-align: right;
      margin-right: 20px;
    }
    
    .amount-value {
      font-size: 14px;
      color: #333;
      
      &.total-price {
        font-size: 18px;
        color: #FF4433;
        font-weight: 600;
      }
    }
  }
  
  .total-row {
    margin-top: 20px;
    padding-top: 20px;
    border-top: 1px dashed #e0e0e0;
  }
}

/* 操作按钮 */
.order-actions {
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-top: 40px;
  
  .el-button {
    padding: 10px 30px;
    font-size: 16px;
    border-radius: 4px;
  }
  
  .el-button--primary {
    background-color: #3E2CCD;
    border-color: #3E2CCD;
    
    &:hover {
      background-color: #3325ad;
      border-color: #3325ad;
    }
  }
}

.countdown-text {
  font-size: 12px;
  margin-left: 4px;
}

.countdown-danger {
  color: #f56c6c !important;
  font-weight: bold;
}

.status-danger {
  color: #f56c6c;
}
</style>
